import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';

// styles
import * as Styles from './styles';

// components
import Header from 'widgets/header';
import { PageLoader } from 'utils/styles';
import LedgerTransactionsHeader from './header';
import FiltersSection from './filtersSection';
import LedgerTable from './ledgerTable';
import LedgerPagination from './pagination';
import ErrorPage from 'widgets/errorScreen';

// constants
import { FilterTag } from './constants/types';

// utils
import { useFetchParams } from 'hooks/useFetchParams';
import { useViewport } from 'utils/responsive';
import { MPosContext } from 'utils/context';
import { getImageBasePath } from 'utils/generics';
import {
    transformGroupedToLedgerRows,
    GroupedLedgerRow,
} from './utils/transformGroupedLedger';

// apis
import { getProductsByPartnerId, getWalletLedger } from './apis';
import type { TxnAggLevel } from './filtersSection/helpers';

const getDateThreeMonthsAgo = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d;
};

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Local calendar date as YYYY-MM-DD (avoids UTC shift from toISOString). */
const formatLocalDateForApi = (date: Date): string =>
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
        date.getDate()
    )}`;

const formatStartTimeForApi = (date: Date): string =>
    `${formatLocalDateForApi(date)}T00:00:00`;

const formatEndTimeForApi = (date: Date): string =>
    `${formatLocalDateForApi(date)}T23:59:59`;

type WalletProduct = {
    product_id: string;
    user_id: string;
    name: string;
};

function WalletTransactions() {
    const { isMobile } = useViewport();
    const router = useRouter();
    const { lob } = useFetchParams();
    const { partnerId, permissions } = useContext(MPosContext);
    const [activeTag, setActiveTag] = useState<FilterTag>(FilterTag.ALL);
    const [currentPage, setCurrentPage] = useState(1);
    const [startDate, setStartDate] = useState(() => getDateThreeMonthsAgo());
    const [endDate, setEndDate] = useState(() => new Date());
    const [appliedStartDate, setAppliedStartDate] = useState(() =>
        getDateThreeMonthsAgo()
    );
    const [appliedEndDate, setAppliedEndDate] = useState(() => new Date());
    const [appliedTxnAggLevel, setAppliedTxnAggLevel] =
        useState<TxnAggLevel>('day');
    const [groupedRows, setGroupedRows] = useState<GroupedLedgerRow[]>([]);
    const [productsList, setProductsList] = useState<WalletProduct[]>([]);
    const [selectedProduct, setSelectedProduct] =
        useState<WalletProduct | null>(null);
    const [isFetchingProductId, setIsFetchingProductId] = useState(true);
    const [isFetchingLedger, setIsFetchingLedger] = useState(false);
    const [totalPagesFromApi, setTotalPagesFromApi] = useState<number | null>(
        null
    );
    const [showError, setShowError] = useState(false);

    useEffect(() => {
        const fetchProductId = async () => {
            if (!partnerId) {
                setIsFetchingProductId(false);
                return;
            }

            const { status, data } = await getProductsByPartnerId(
                partnerId,
                permissions.group
            );
            if (status && data) {
                const products: WalletProduct[] = Array.isArray(data)
                    ? data
                    : data?.products || [];
                setProductsList(products);
                if (products.length > 0) {
                    setSelectedProduct(products[0]);
                }
            } else {
                setShowError(true);
            }
            setIsFetchingProductId(false);
        };

        fetchProductId();
    }, [partnerId]);

    useEffect(() => {
        if (!partnerId || isFetchingProductId || !selectedProduct) return;

        const fetchLedger = async () => {
            setIsFetchingLedger(true);
            try {
                const { status, data } = await getWalletLedger(
                    partnerId,
                    {
                        page: currentPage,
                        startTime: formatStartTimeForApi(appliedStartDate),
                        endTime: formatEndTimeForApi(appliedEndDate),
                        productId: selectedProduct.product_id,
                        walletUserId: selectedProduct.user_id,
                        txnAggLevel: appliedTxnAggLevel,
                    },
                    permissions.group
                );

                if (status && data) {
                    const payload: any = data;
                    const groupedTxnSummaries =
                        payload?.groupedTxnSummaries ||
                        payload?.data?.groupedTxnSummaries ||
                        [];
                    const totalPageNumbers =
                        payload?.totalPageNumbers?.value ||
                        payload?.data?.totalPageNumbers?.value ||
                        1;
                    setGroupedRows(
                        transformGroupedToLedgerRows(groupedTxnSummaries)
                    );
                    setTotalPagesFromApi(totalPageNumbers);
                } else {
                    setGroupedRows([]);
                    setTotalPagesFromApi(1);
                }
            } finally {
                setIsFetchingLedger(false);
            }
        };

        fetchLedger();
    }, [
        partnerId,
        permissions.group,
        currentPage,
        appliedStartDate,
        appliedEndDate,
        appliedTxnAggLevel,
        selectedProduct,
        isFetchingProductId,
    ]);

    const handleApplyFilters = (payload: {
        startDate: Date;
        endDate: Date;
        txnAggLevel: TxnAggLevel;
    }) => {
        setAppliedStartDate(payload.startDate);
        setAppliedEndDate(payload.endDate);
        setAppliedTxnAggLevel(payload.txnAggLevel);
        setCurrentPage(1);
        if (payload.txnAggLevel === 'month') {
            setStartDate(payload.startDate);
            setEndDate(payload.endDate);
        }
    };

    const totalPages = totalPagesFromApi ?? 1;

    const onGroupByTag = (tag: FilterTag) => {
        setActiveTag(tag);
        setCurrentPage(1);
    };

    const onBack = () => {
        router.push(`/${lob}/${partnerId}/dashboard`);
    };

    if (isFetchingProductId) {
        return <PageLoader src={getImageBasePath('/gif/pageLoader.gif')} />;
    }

    if (showError) {
        return <ErrorPage hideHeader={true} />;
    }

    return (
        <Styles.Container>
            <Header
                wrapperStyles={{
                    maxWidth: isMobile ? '100%' : '95%',
                    margin: '0 auto',
                    padding: '20px',
                }}
            />
            <Styles.SectionDivider />
            <Styles.SectionsCard>
                <Styles.TitleRow>
                    <Styles.BackButton>
                        <Image
                            width={'10px'}
                            height={'24px'}
                            alt={''}
                            src={getImageBasePath('/icons/backArrow.svg')}
                            onClick={onBack}
                        />
                    </Styles.BackButton>
                    <Styles.PageTitle>Wallet Transactions</Styles.PageTitle>
                </Styles.TitleRow>
                <LedgerTransactionsHeader
                    startDate={appliedStartDate}
                    endDate={appliedEndDate}
                    productsList={productsList}
                    selectedProduct={selectedProduct}
                    setSelectedProduct={setSelectedProduct}
                />
                <FiltersSection
                    activeTag={activeTag}
                    onGroupByTag={onGroupByTag}
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    onApplyFilters={handleApplyFilters}
                />
                <LedgerTable
                    key={activeTag}
                    rows={groupedRows}
                    activeTag={activeTag}
                    isGrouped
                    isLoading={isFetchingLedger}
                />
                <LedgerPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    rowsPerPage={undefined}
                    onPageChange={setCurrentPage}
                />
            </Styles.SectionsCard>
        </Styles.Container>
    );
}

export default WalletTransactions;
